import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { addDeviceAPI, refreshDeviceAPI, resetDeviceAPI } from '@/api/user'
import UserStore from '@/store/user'
import { readNotificationAPI } from '@/api/notifications'
import { getDeviceId, getDeviceName } from './useDevice'
import ToastsStore from '@/store/toasts'
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings'

export const notificationsEnable = async () => {
  const toastsStore = ToastsStore()
  
  try {
    let permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions()
    }

    if (permStatus.receive !== 'granted') {
      throw new Error('User denied permissions!')
    }

    await PushNotifications.register()

    return true
  } catch (e) {
    console.error(e)

    toastsStore.add({
      type: 'failure',
      content: useNuxtApp().$i18n.t('components.Notifications.error'),
    })

    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails, 
      optionIOS: IOSSettings.App
    })

    return false
  }
}

export const notificationStatusGet = async () => {
  if (!Capacitor.isPluginAvailable('PushNotifications')) return false

  const userStore = UserStore()
  const { currentUser } = storeToRefs(userStore)
  const deviceId = await getDeviceId()

  const isDevice = (currentUser.value?.devices ?? []).find(d => d.id === deviceId && d.isActive)
  const status = await PushNotifications.checkPermissions()

  console.log('NOTIFICATION STATUS:' + isDevice, status)
  
  return status.receive === 'granted' && isDevice ? true : false
}

export const notificationsCanEnable = () => {  
  const canEnable = ref(false)
  const userStore = UserStore()
  const { currentUser } = storeToRefs(userStore)

  watch(() => currentUser.value?.devices, async () => {
    if (!Capacitor.isPluginAvailable('PushNotifications')) return false
    
    const permissionState = await PushNotifications.checkPermissions()
    const deviceId = await getDeviceId()

    canEnable.value = !currentUser.value?.devices.find(d => d.id === deviceId && d.isActive) || permissionState.receive !== 'granted'
  }, { immediate: true })

  return canEnable
}

export const notificationsCanEnable2 = async () => {  
  if (!Capacitor.isPluginAvailable('PushNotifications')) return false
  
  return true
}

export const notificationsTokenRefresh = async () => {
  await PushNotifications.register()
}

export const notificationsInit = async () => {
  if (!Capacitor.isPluginAvailable('PushNotifications')) return

  const userStore = UserStore()
  const { currentDevice } = storeToRefs(userStore)

  await PushNotifications.removeAllListeners()

  PushNotifications.createChannel({
    description: '',
    id: 'main',
    importance: 5,
    sound: 'message_received.wav',
    vibration: true,
    visibility: 1,
    name: 'Notifications Vibes'
  })

  PushNotifications.addListener('registration', async (token: { value: string }) => {
    if (currentDevice.value) {
      await refreshDeviceAPI(currentDevice.value.id, token.value)
    } else {
      await addDeviceAPI({
        id: await getDeviceId(),
        name: await getDeviceName(),
        token: token.value
      })
    }

    await userStore.updateJourney('is-notifications')
    await userStore.initUser(true)

    const toastsStore = ToastsStore()

    toastsStore.add({
      type: 'success',
      content: useNuxtApp().$i18n.t('components.Notifications.success'),
    })
  })

  PushNotifications.addListener('registrationError', (err: any) => {
    console.error('Registration error: ', err.error);
  })

  PushNotifications.addListener('pushNotificationActionPerformed', async (notification: any) => {
    const router = useRouter()

    if (notification.notification.data?.link) {
      if (notification.notification.data?._id) readNotificationAPI({ type: 'read', notifications: [notification.notification.data._id] })
      
      router.push(notification.notification.data?.link)
    }
  })
}

export const notificationsReset = async () => {
  if (!Capacitor.isPluginAvailable('PushNotifications')) return

  const userStore = UserStore()
  
  await resetDeviceAPI()
  await userStore.initUser(true)
  await notificationsEnable()

  return
}

export const notificationsRemove = async (id: string) => {
  if (!Capacitor.isPluginAvailable('PushNotifications')) return true

  try {
    const { notifications } = await PushNotifications.getDeliveredNotifications()

    notifications.filter(n => n?.tag === id).forEach(notification => {
      PushNotifications.removeDeliveredNotifications({ notifications: [ notification ]})
    })
  } catch (e) {
    console.error(e)
  }

  return true
}